// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, AgeRow } from '../types';
import { num, rate, pct, esc, COL } from '../format';
import { gloss } from '../glossary';
import { tipAttr, niceMax, linePoints } from '../charts';

export function renderDemographics(root: HTMLElement, data: Dataset): void {
  const { age, meta } = data;
  const c = meta.counts;
  const ages = age.ages;
  const peak = ages.reduce((a, b) => ((b.personsN ?? 0) > (a.personsN ?? 0) ? b : a), ages[0]);
  const mfRatio = c.femaleRate ? c.maleRate / c.femaleRate : null;

  root.innerHTML = `
    <div class="view-head">
      <h2>Who is inside</h2>
      <p>Imprisonment is sharply shaped by age and sex: it peaks in the early thirties and men are imprisoned far more often than women. This is the makeup of the ${num(c.total)} people in prison on 30 June ${meta.latestYear}.</p>
    </div>

    <div class="stat-row">
      <div class="stat"><div class="stat-label">Men</div><div class="stat-value">${num(c.malesN)}</div><div class="stat-note">${pct(c.malesN / c.total)} of prisoners</div></div>
      <div class="stat"><div class="stat-label">Women</div><div class="stat-value">${num(c.femalesN)}</div><div class="stat-note">${pct(c.femalesN / c.total)} of prisoners</div></div>
      <div class="stat"><div class="stat-label">Male vs female rate</div><div class="stat-value">${mfRatio ? mfRatio.toFixed(1) + '×' : '—'}</div><div class="stat-note">${rate(c.maleRate, 0)} vs ${rate(c.femaleRate, 0)} per 100k</div></div>
      <div class="stat"><div class="stat-label">Peak age group</div><div class="stat-value">${esc(peak.age.replace(' years', ''))}</div><div class="stat-note">${num(peak.personsN)} prisoners</div></div>
      <div class="stat"><div class="stat-label">${gloss('prior', 'Prior imprisonment')}</div><div class="stat-value">${pct(c.priorShare)}</div><div class="stat-note">have been inside before</div></div>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Prisoners by age and sex</h3><p>Number of prisoners in each age group, men to the left, women to the right, on one scale.</p></div>
      <div class="chart-wrap" id="dem-pyramid"></div>
      <div class="legend"><span class="legend-item"><span class="legend-swatch" style="background:${COL.male}"></span>Men</span><span class="legend-item"><span class="legend-swatch" style="background:${COL.female}"></span>Women</span></div>
    </div>

    <div class="two-col" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-head"><h3>Imprisonment rate by age</h3><p>Per 100,000 people of that age and sex — the shape counts, not just the numbers.</p></div>
        <div class="chart-wrap" id="dem-rate"></div>
        <div class="legend"><span class="legend-item"><span class="legend-swatch" style="background:${COL.male}"></span>Men</span><span class="legend-item"><span class="legend-swatch" style="background:${COL.female}"></span>Women</span></div>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>The make-up of the prison population</h3><p>${meta.latestYear}.</p></div>
        <div id="dem-comp"></div>
      </div>
    </div>
  `;

  drawPyramid(root.querySelector('#dem-pyramid') as HTMLElement, ages);
  drawRateByAge(root.querySelector('#dem-rate') as HTMLElement, ages);
  drawComposition(root.querySelector('#dem-comp') as HTMLElement, data);
}

function drawPyramid(el: HTMLElement, ages: AgeRow[]): void {
  const W = 900, rowH = 26, top = 10, bottom = 24, mid = W / 2, gap = 64;
  const H = top + ages.length * rowH + bottom;
  const half = (W - gap) / 2;
  const maxN = Math.max(...ages.flatMap((a) => [a.malesN ?? 0, a.femalesN ?? 0]));
  const scale = (v: number) => (v / maxN) * half;
  let bars = '';
  ages.forEach((a, i) => {
    const cy = top + i * rowH;
    const mW = scale(a.malesN ?? 0), fW = scale(a.femalesN ?? 0);
    bars += `<g>
      <rect class="bar" x="${(mid - gap / 2 - mW).toFixed(1)}" y="${(cy + 4).toFixed(1)}" width="${mW.toFixed(1)}" height="${rowH - 8}" fill="${COL.male}" rx="2" ${tipAttr(`${a.age}\nMen: ${num(a.malesN)}`)} aria-label="Men aged ${esc(a.age)}: ${num(a.malesN)}"></rect>
      <rect class="bar" x="${(mid + gap / 2).toFixed(1)}" y="${(cy + 4).toFixed(1)}" width="${fW.toFixed(1)}" height="${rowH - 8}" fill="${COL.female}" rx="2" ${tipAttr(`${a.age}\nWomen: ${num(a.femalesN)}`)} aria-label="Women aged ${esc(a.age)}: ${num(a.femalesN)}"></rect>
      <text class="axis-text" x="${mid}" y="${cy + rowH / 2 + 3}" text-anchor="middle" style="fill:var(--text-primary)">${esc(a.age.replace(' years', '').replace(' and over', '+'))}</text>
    </g>`;
  });
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Population pyramid of prisoners by age and sex">${bars}</svg>`;
}

function drawRateByAge(el: HTMLElement, ages: AgeRow[]): void {
  const W = 440, H = 260, m = { t: 12, r: 14, b: 46, l: 44 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const maxV = niceMax(Math.max(...ages.flatMap((a) => [a.maleRate ?? 0, a.femaleRate ?? 0])));
  const x = (i: number) => m.l + (i / (ages.length - 1)) * iw;
  const y = (v: number) => m.t + ih - (v / maxV) * ih;
  const ticks = 4;
  const gridY = Array.from({ length: ticks + 1 }, (_, i) => (maxV / ticks) * i);
  const series = (get: (a: AgeRow) => number | null, col: string, label: string) => {
    const vals = ages.map(get);
    return `<polyline class="series-line" points="${linePoints(vals, x, y)}" stroke="${col}"/>` +
      vals.map((v, i) => (v == null ? '' : `<circle class="dot" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="6" fill="transparent" ${tipAttr(`${ages[i].age}\n${label}: ${rate(v, 0)} per 100k`)} aria-label="${esc(ages[i].age)} ${esc(label)}: ${rate(v, 0)}"></circle><circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="2.2" fill="${col}"/>`)).join('');
  };
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Imprisonment rate by age and sex">
    ${gridY.map((g) => `<line class="grid-line" x1="${m.l}" y1="${y(g).toFixed(1)}" x2="${W - m.r}" y2="${y(g).toFixed(1)}"/><text class="axis-text" x="${m.l - 6}" y="${y(g) + 3}" text-anchor="end">${num(g)}</text>`).join('')}
    ${ages.map((a, i) => (i % 2 === 0 || i === ages.length - 1 ? `<text class="axis-text" x="${x(i).toFixed(1)}" y="${H - m.b + 12}" text-anchor="end" transform="rotate(-40 ${x(i).toFixed(1)} ${H - m.b + 12})">${esc(a.age.replace(' years', '').replace(' and over', '+'))}</text>` : '')).join('')}
    ${series((a) => a.femaleRate, COL.female, 'Women')}
    ${series((a) => a.maleRate, COL.male, 'Men')}
  </svg>`;
}

function drawComposition(el: HTMLElement, data: Dataset): void {
  const c = data.meta.counts;
  const rows = [
    { label: 'Men', v: c.malesN / c.total, col: COL.male },
    { label: 'Women', v: c.femalesN / c.total, col: COL.female },
    { label: 'Sentenced', v: 1 - c.remandShare, col: COL.sentenced },
    { label: 'On remand', v: c.remandShare, col: COL.remand },
    { label: 'Aboriginal & Torres Strait Islander', v: c.indigShare, col: COL.atsi },
    { label: 'Non-Indigenous', v: 1 - c.indigShare, col: COL.nonIndig },
    { label: 'Prior imprisonment', v: c.priorShare, col: COL.prior },
  ];
  el.className = 'barlist';
  el.innerHTML = rows.map((r) => `
    <div class="barrow" style="cursor:default;grid-template-columns:minmax(0,200px) 1fr auto" ${tipAttr(`${r.label}: ${pct(r.v)}`)} aria-label="${esc(r.label)}: ${pct(r.v)}">
      <span class="lbl">${esc(r.label)}</span>
      <span class="track"><span style="width:${(r.v * 100).toFixed(1)}%;background:${r.col}"></span></span>
      <span class="val">${pct(r.v)}</span>
    </div>`).join('');
}
